import React, { CSSProperties, useEffect, useMemo, useRef } from "react";
import { classArr } from "../helper/style-helper";
import { useDeviceModels } from "../components/main/device-model-provider";
import * as styles from "./device-icon.module.scss";
import * as globals from "../styles/global.icss.scss";

export default function DeviceIcon(props: {
	className?: string;
	style?: CSSProperties;
	color?: string;
	size?: number;
	model?: string | undefined;
}) {
	const { getDeviceModel } = useDeviceModels();

	//Get the device model information
	const deviceModel = getDeviceModel(props.model);

	const size = props.size ?? 16;
	const color = props.color ?? globals.textPrimary;

	//Download the device icon
	const [svgRootElement, setSvgRootElement] =
		React.useState<HTMLElement | null>(null);
	useMemo(() => {
		setSvgRootElement(null);

		if (deviceModel === undefined) {
			return;
		}

		//Fetch the icon
		fetchSvgHtmlElement(deviceModel.images.icon)
			.then(setSvgRootElement)
			.catch(console.warn);
	}, [deviceModel]);

	//Update the icon properties
	useEffect(() => {
		if (svgRootElement === null) {
			return;
		}

		//Update the icon color
		svgRootElement.setAttribute("fill", color);

		//Update the icon size
		const sizePx = size + "px";
		svgRootElement.setAttribute("width", sizePx);
		svgRootElement.setAttribute("height", sizePx);
	}, [svgRootElement, color, size]);

	//Add the icon to the wrapper
	const wrapperRef = useRef<HTMLDivElement | null>(null);
	useEffect(() => {
		if (svgRootElement === null) {
			return;
		}

		wrapperRef.current!.innerHTML = "";
		wrapperRef.current!.appendChild(svgRootElement);
	}, [svgRootElement]);

	return (
		<div
			ref={wrapperRef}
			style={{ width: size, height: size, ...(props.style ?? {}) }}
			className={classArr(props.className, styles.wrapper)}
			title={props.model}
		></div>
	);
}

async function fetchSvgHtmlElement(url: string) {
	//Fetch the SVG from the URL
	const response = await fetch(url);
	if (!response.ok) {
		throw new Error(
			`Failed to fetch SVG: ${response.status} ${response.statusText}`,
		);
	}

	//Get the SVG content as text
	const svgString = await response.text();

	//Create a DOM parser to work with the SVG
	const parser = new DOMParser();
	const svgDoc = parser.parseFromString(svgString, "image/svg+xml");
	return svgDoc.documentElement;
}
